import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  getCloudStatus, getCloudContract, acceptCloudContract,
  disconnectCloud, getCloudAudit, getCloudProof, onedriveConnectUrl, cloudSyncNow,
} from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProgereBrand } from "@/components/ProgereBrand";
import CloudSyncPolicies from "@/components/cloud/CloudSyncPolicies";
import MsActivation from "@/components/cloud/MsActivation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Cloud, ShieldCheck, Lock, FileSignature, CheckCircle2, XCircle,
  Eraser, Download, PlugZap, Unplug, ScrollText, AlertTriangle, Loader2, RefreshCw,
} from "lucide-react";

/* -------- Signature manuscrite (souris + tactile) -------- */
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const has = useRef(false);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - r.left;
    const y = (t ? t.clientY : e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#f5f5f5";
    ctx.lineTo(x, y);
    ctx.stroke();
    has.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (has.current) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    has.current = false;
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={640}
        height={180}
        data-testid="cc-signature-canvas"
        className="w-full h-[180px] rounded-lg border border-white/15 bg-black/50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-white/40">Signez ci-dessus avec la souris ou le doigt</span>
        <Button data-testid="cc-signature-clear" onClick={clear}
          className="h-8 bg-transparent border border-white/15 text-white/60 text-xs gap-1.5 hover:text-[#dc143c] hover:border-[#dc143c]/40">
          <Eraser className="w-3.5 h-3.5" /> Effacer
        </Button>
      </div>
    </div>
  );
}

/* ------------------------- Page ------------------------- */
export default function CloudConnectPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const canManage = user && ["owner", "admin", "platform_owner"].includes(user.role);

  const [status, setStatus] = useState(null);
  const [contract, setContract] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const [checks, setChecks] = useState([]);
  const [fullName, setFullName] = useState(user?.name || "");
  const [title, setTitle] = useState("");
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msActive, setMsActive] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, ct] = await Promise.all([getCloudStatus(), getCloudContract()]);
      setStatus(st);
      setContract(ct);
      setChecks((prev) => (prev.length === ct.confirmations.length ? prev : ct.confirmations.map(() => false)));
      if (canManage) getCloudAudit(50).then(setAudit).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const r = params.get("onedrive");
    if (!r) return;
    if (r === "connected") toast.success("OneDrive connecté avec succès.");
    else if (r === "state_invalid") toast.error("Session OAuth expirée. Réessayez la connexion.");
    else toast.error("La connexion OneDrive a échoué. Réessayez.");
    params.delete("onedrive");
    setParams(params, { replace: true });
    load();
  }, [params, setParams, load]);

  const allChecked = checks.length > 0 && checks.every(Boolean);
  const canSubmit = allChecked && fullName.trim() && title.trim() && signature.length > 100 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await acceptCloudContract({
        provider: "onedrive", full_name: fullName.trim(), title: title.trim(),
        signature, confirmations: checks,
      });
      toast.success("Contrat PROGÈRE CLOUD CONNECT signé.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const connect = () => { window.location.href = onedriveConnectUrl(); };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await cloudSyncNow("onedrive");
      toast.success(`Synchronisation terminée (${res.items} élément(s) indexé(s)).`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectCloud("onedrive");
      toast.success("OneDrive déconnecté.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDisconnecting(false);
    }
  };

  const downloadProof = async () => {
    try {
      const proof = await getCloudProof(status.acceptance_id);
      const blob = new Blob([JSON.stringify(proof, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `preuve-cloud-connect-${status.acceptance_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="cc-loading">
        <Loader2 className="w-6 h-6 text-[#EAB308] animate-spin" />
      </div>
    );
  }

  const badge = status?.connected
    ? { label: "Connecté", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300", icon: CheckCircle2 }
    : status?.contract_accepted
      ? { label: "Déconnecté", cls: "bg-white/10 border-white/20 text-white/60", icon: XCircle }
      : { label: "Contrat requis", cls: "bg-[#dc143c]/15 border-[#dc143c]/40 text-[#ff8fa3]", icon: FileSignature };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="cloud-connect-page">
      {/* En-tête */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <Cloud className="w-6 h-6 text-[#EAB308]" />
            <ProgereBrand module="CLOUD CONNECT" size="medium" />
          </div>
          <p className="mt-2 text-white/55 max-w-2xl">
            Connectez votre stockage infonuagique à l'écosystème PROGÈRE. Vos documents, votre cloud, votre contrôle.
          </p>
        </div>
        <span data-testid="cc-status-badge"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${badge.cls}`}>
          <badge.icon className="w-3.5 h-3.5" /> {badge.label}
        </span>
      </div>

      {/* Bandeau juridique */}
      <div className="rounded-lg border border-[#EAB308]/25 bg-[#EAB308]/[0.05] p-4 flex gap-3" data-testid="cc-legal-banner">
        <ShieldCheck className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
        <p className="text-sm text-white/70">
          PROGÈRE et Groupe MJR Construction inc. agissent uniquement comme <strong className="text-white">connecteur technologique</strong>.
          Votre cloud, votre abonnement et vos sauvegardes vous appartiennent. Votre mot de passe externe n'est jamais conservé.
        </p>
      </div>

      {/* ---------- Contrat non signé ---------- */}
      {!status?.contract_accepted && contract && (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6 space-y-6" data-testid="cc-contract-card">
          <div className="flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-[#EAB308]" />
            <h2 className="text-lg font-bold text-white">Contrat PROGÈRE CLOUD CONNECT — v{contract.version}</h2>
          </div>

          <pre data-testid="cc-contract-text"
            className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-white/10 bg-black/40 p-4 text-xs leading-relaxed text-white/70 font-sans">
            {contract.text}
          </pre>

          {!canManage ? (
            <div className="flex items-center gap-2 text-sm text-[#ff8fa3]">
              <AlertTriangle className="w-4 h-4" /> Seul le propriétaire ou un administrateur peut signer ce contrat.
            </div>
          ) : (
            <>
              <div className="space-y-3" data-testid="cc-confirmations">
                {contract.confirmations.map((c, i) => (
                  <label key={i} data-testid={`cc-confirm-${i}`}
                    className="flex items-start gap-3 rounded-md border border-white/10 bg-black/30 p-3 cursor-pointer hover:border-white/20">
                    <Checkbox
                      checked={checks[i] || false}
                      onCheckedChange={(v) => setChecks((prev) => prev.map((x, j) => (j === i ? !!v : x)))}
                      className="mt-0.5 border-white/30 data-[state=checked]:bg-[#EAB308] data-[state=checked]:border-[#EAB308]"
                    />
                    <span className="text-sm text-white/75">{c}</span>
                  </label>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-white/80">Nom complet</Label>
                  <Input data-testid="cc-full-name" value={fullName} onChange={(e) => setFullName(e.target.value)}
                    className="bg-black/40 border-white/15 text-white h-11" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Titre / fonction</Label>
                  <Input data-testid="cc-title" value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex. Président, Directeur" className="bg-black/40 border-white/15 text-white h-11" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80 flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-[#EAB308]" /> Signature manuscrite électronique
                </Label>
                <SignaturePad onChange={setSignature} />
              </div>

              <div className="flex items-center justify-between gap-4 pt-2">
                <span className="text-xs text-white/40 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Empreinte SHA-256 et horodatage enregistrés comme preuve.
                </span>
                <Button data-testid="cc-sign-submit" onClick={submit} disabled={!canSubmit}
                  className="h-11 px-6 bg-[#EAB308] text-black font-semibold rounded-md hover:opacity-90 disabled:opacity-40">
                  {submitting ? "Signature…" : "Signer et accepter"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------- Contrat signé : OneDrive ---------- */}
      {status?.contract_accepted && (
        <>
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-5 flex flex-wrap items-center justify-between gap-3" data-testid="cc-accepted-card">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <div>
                <div className="text-sm font-semibold text-white">Contrat signé</div>
                <div className="text-xs text-white/50 font-mono">{status.acceptance_id}</div>
              </div>
            </div>
            <Button data-testid="cc-download-proof" onClick={downloadProof}
              className="h-9 bg-white/10 border border-white/15 text-white text-xs gap-1.5 hover:bg-white/15">
              <Download className="w-3.5 h-3.5" /> Télécharger la preuve
            </Button>
          </div>

          <MsActivation onActive={setMsActive} />

          {msActive && (
          <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6" data-testid="cc-onedrive-card">
            <div className="flex items-center gap-3">
              <img src="https://img.icons8.com/color/48/microsoft-onedrive-2019.png" alt="OneDrive" className="h-8 w-8"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <div>
                <h2 className="text-lg font-bold text-white">Microsoft OneDrive</h2>
                <p className="text-xs text-white/45">OAuth 2.0 + PKCE · jetons chiffrés AES-256 · permissions minimales</p>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2.5 rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid="cc-no-azure-notice">
              <ShieldCheck className="w-4 h-4 text-[#EAB308] shrink-0 mt-0.5" />
              <p className="text-xs text-white/55">
                PROGÈRE n'est pas hébergé sur Azure. Microsoft Entra est utilisé uniquement pour autoriser sécuritairement
                la connexion à votre OneDrive. Vos fichiers demeurent dans votre propre compte Microsoft.
              </p>
            </div>

            {status.connected ? (
              <div className="mt-5 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 text-sm">
                  <div className="rounded-md border border-white/10 bg-black/30 p-3">
                    <div className="text-white/40 text-xs">Compte</div>
                    <div className="text-white truncate" data-testid="cc-account-email">{status.account_email || "—"}</div>
                  </div>
                  <div className="rounded-md border border-white/10 bg-black/30 p-3">
                    <div className="text-white/40 text-xs">Type</div>
                    <div className="text-white capitalize">{status.account_type || "—"}</div>
                  </div>
                </div>
                {canManage && (
                  <Button data-testid="cc-disconnect" onClick={disconnect} disabled={disconnecting}
                    className="h-10 bg-transparent border border-[#dc143c]/40 text-[#ff8fa3] gap-2 hover:bg-[#dc143c]/10">
                    <Unplug className="w-4 h-4" /> {disconnecting ? "Déconnexion…" : "Déconnecter OneDrive"}
                  </Button>
                )}
                {canManage && (
                  <Button data-testid="cc-sync-now" onClick={syncNow} disabled={syncing}
                    className="h-10 ml-2 bg-[#EAB308] text-black font-semibold gap-2 hover:opacity-90">
                    {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {syncing ? "Synchronisation…" : "Synchroniser maintenant"}
                  </Button>
                )}
              </div>
            ) : (
              <div className="mt-5">
                {status.onedrive_configured ? (
                  <Button data-testid="cc-connect-onedrive" onClick={connect} disabled={!canManage}
                    className="h-11 px-6 bg-[#EAB308] text-black font-semibold rounded-md hover:opacity-90 gap-2 disabled:opacity-40">
                    <PlugZap className="w-4 h-4" /> Connecter OneDrive
                  </Button>
                ) : (
                  <div className="flex items-start gap-3 rounded-md border border-white/10 bg-black/30 p-4" data-testid="cc-onedrive-pending">
                    <AlertTriangle className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
                    <div className="text-sm text-white/60">
                      La connexion OneDrive sera activée dès que la configuration Microsoft Entra sera finalisée par l'administrateur PROGÈRE.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </>
      )}

      {/* ---------- Politiques de synchronisation ---------- */}
      {status?.contract_accepted && (
        <CloudSyncPolicies canManage={canManage} />
      )}

      {/* ---------- Journal d'audit ---------- */}
      {canManage && audit.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6" data-testid="cc-audit-card">
          <h2 className="text-lg font-bold text-white mb-4">Journal d'audit</h2>
          <div className="space-y-2">
            {audit.slice(0, 15).map((a, i) => (
              <div key={i} data-testid={`cc-audit-row-${i}`}
                className="flex items-center justify-between rounded-md border border-white/5 bg-black/30 px-3 py-2 text-xs">
                <span className="text-white/70 font-mono">{a.action}</span>
                <span className="text-white/35">{new Date(a.at).toLocaleString("fr-CA")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
